import { defineMutator } from "@rocicorp/zero"

import type { ZeroJwtPayload } from "@productlane/lib/src/browser"
import {
	CUSTOM_SECTION_TILES,
	MAX_CUSTOM_HOME_SECTIONS,
	PICKED_SECTION_ARTICLE_LIMIT,
} from "@productlane/lib/src/help-center-home-sections"

import type { MutatorTx } from "../schema"
import type {
	HelpCenterCustomHomeSectionArticleSet,
	HelpCenterCustomHomeSectionFolderChange,
	HelpCenterCustomHomeSectionMove,
	HelpCenterCustomHomeSectionRename,
	HelpCenterFolderHomeSectionCreate,
	HelpCenterPickedHomeSectionCreate,
} from "../types"

interface Ctx {
	tx: MutatorTx
	ctx: ZeroJwtPayload
}

async function findOwnSection({ tx, ctx }: Ctx, id: string) {
	const [section] = await tx.query.helpCenterCustomHomeSections
		.where("id", id)
		.where("workspaceId", ctx.workspaceId)
		.run()
	if (!section) throw new Error("Section not found.")
	return section
}

async function findOwnGroup({ tx, ctx }: Ctx, id: string) {
	const [group] = await tx.query.helpCenterGroups
		.where("id", id)
		.where("workspaceId", ctx.workspaceId)
		.run()
	if (!group) throw new Error("Folder not found.")
	return group
}

async function portalSections(
	{ tx, ctx }: Ctx,
	portalInstanceId: string | null,
) {
	const q = tx.query.helpCenterCustomHomeSections.where(
		"workspaceId",
		ctx.workspaceId,
	)
	return portalInstanceId
		? q.where("portalInstanceId", portalInstanceId).run()
		: q.where("portalInstanceId", "IS", null).run()
}

async function nextSectionOrder(c: Ctx, portalInstanceId: string | null) {
	if (portalInstanceId) {
		const [portal] = await c.tx.query.portalInstances
			.where("id", portalInstanceId)
			.where("workspaceId", c.ctx.workspaceId)
			.run()
		if (!portal) throw new Error("Portal not found.")
	}
	const sections = await portalSections(c, portalInstanceId)
	if (sections.length >= MAX_CUSTOM_HOME_SECTIONS) {
		throw new Error(
			`The docs home holds ${MAX_CUSTOM_HOME_SECTIONS} custom sections`,
		)
	}
	return { sections, order: Math.max(-1, ...sections.map((s) => s.order)) + 1 }
}

// A folder only renders on its own portal's home, so it cannot be picked elsewhere.
function assertFolderOnPortal(
	group: { portalInstanceId?: string | null },
	portalInstanceId: string | null,
) {
	if ((group.portalInstanceId ?? null) !== portalInstanceId) {
		throw new Error("That folder belongs to another portal.")
	}
}

export const helpCenterCustomHomeSectionMutators = {
	addFolder: defineMutator(
		async ({
			tx,
			args,
			ctx,
		}: {
			tx: MutatorTx
			args: HelpCenterFolderHomeSectionCreate
			ctx: ZeroJwtPayload
		}) => {
			const group = await findOwnGroup({ tx, ctx }, args.groupId)
			assertFolderOnPortal(group, args.portalInstanceId)
			const { sections, order } = await nextSectionOrder(
				{ tx, ctx },
				args.portalInstanceId,
			)
			if (sections.some((section) => section.groupId === args.groupId)) {
				throw new Error("That folder is already on the docs home.")
			}
			const now = Date.now()
			await tx.mutate.helpCenterCustomHomeSections.insert({
				id: args.id,
				name: null,
				order,
				articleLimit: CUSTOM_SECTION_TILES,
				workspaceId: ctx.workspaceId,
				portalInstanceId: args.portalInstanceId,
				groupId: args.groupId,
				createdAt: now,
				updatedAt: now,
			})
		},
	),
	addPicked: defineMutator(
		async ({
			tx,
			args,
			ctx,
		}: {
			tx: MutatorTx
			args: HelpCenterPickedHomeSectionCreate
			ctx: ZeroJwtPayload
		}) => {
			const name = args.name.trim()
			if (!name) throw new Error("A section needs a name.")
			const { order } = await nextSectionOrder(
				{ tx, ctx },
				args.portalInstanceId,
			)
			const now = Date.now()
			await tx.mutate.helpCenterCustomHomeSections.insert({
				id: args.id,
				name,
				order,
				articleLimit: CUSTOM_SECTION_TILES,
				workspaceId: ctx.workspaceId,
				portalInstanceId: args.portalInstanceId,
				groupId: null,
				createdAt: now,
				updatedAt: now,
			})
		},
	),
	rename: defineMutator(
		async ({
			tx,
			args,
			ctx,
		}: {
			tx: MutatorTx
			args: HelpCenterCustomHomeSectionRename
			ctx: ZeroJwtPayload
		}) => {
			const section = await findOwnSection({ tx, ctx }, args.id)
			const name = args.name?.trim() || null
			if (!name && !section.groupId) {
				throw new Error("A section needs a name.")
			}
			await tx.mutate.helpCenterCustomHomeSections.update({
				id: args.id,
				name,
				updatedAt: Date.now(),
			})
		},
	),
	changeFolder: defineMutator(
		async ({
			tx,
			args,
			ctx,
		}: {
			tx: MutatorTx
			args: HelpCenterCustomHomeSectionFolderChange
			ctx: ZeroJwtPayload
		}) => {
			const section = await findOwnSection({ tx, ctx }, args.id)
			if (!section.groupId) {
				throw new Error("Only a folder section can change folder.")
			}
			const group = await findOwnGroup({ tx, ctx }, args.groupId)
			const portalInstanceId = section.portalInstanceId ?? null
			assertFolderOnPortal(group, portalInstanceId)
			const sections = await portalSections({ tx, ctx }, portalInstanceId)
			if (
				sections.some(
					(other) => other.id !== section.id && other.groupId === args.groupId,
				)
			) {
				throw new Error("That folder is already on the docs home.")
			}
			await tx.mutate.helpCenterCustomHomeSections.update({
				id: args.id,
				groupId: args.groupId,
				updatedAt: Date.now(),
			})
		},
	),
	move: defineMutator(
		async ({
			tx,
			args,
			ctx,
		}: {
			tx: MutatorTx
			args: HelpCenterCustomHomeSectionMove
			ctx: ZeroJwtPayload
		}) => {
			const section = await findOwnSection({ tx, ctx }, args.id)
			const ordered = (
				await portalSections({ tx, ctx }, section.portalInstanceId ?? null)
			).sort((a, b) => a.order - b.order || a.createdAt - b.createdAt)
			const from = ordered.findIndex((other) => other.id === section.id)
			const to = from + (args.direction === "up" ? -1 : 1)
			if (to < 0 || to >= ordered.length) return
			;[ordered[from], ordered[to]] = [ordered[to]!, ordered[from]!]
			// Renumbered from 0, so sections that shared an order stop tying.
			const now = Date.now()
			for (const [order, row] of ordered.entries()) {
				if (row.order === order) continue
				await tx.mutate.helpCenterCustomHomeSections.update({
					id: row.id,
					order,
					updatedAt: now,
				})
			}
		},
	),
	remove: defineMutator(
		async ({
			tx,
			args,
			ctx,
		}: {
			tx: MutatorTx
			args: { id: string }
			ctx: ZeroJwtPayload
		}) => {
			await findOwnSection({ tx, ctx }, args.id)
			const picked = await tx.query.helpCenterArticles
				.where("workspaceId", ctx.workspaceId)
				.where("customHomeSectionId", args.id)
				.run()
			for (const article of picked) {
				await tx.mutate.helpCenterArticles.update({
					id: article.id,
					customHomeSectionId: null,
					homeOrder: null,
				})
			}
			await tx.mutate.helpCenterCustomHomeSections.delete({ id: args.id })
		},
	),
	setArticle: defineMutator(
		async ({
			tx,
			args,
			ctx,
		}: {
			tx: MutatorTx
			args: HelpCenterCustomHomeSectionArticleSet
			ctx: ZeroJwtPayload
		}) => {
			const [article] = await tx.query.helpCenterArticles
				.where("id", args.articleId)
				.where("workspaceId", ctx.workspaceId)
				.run()
			if (!article) throw new Error("Article not found.")

			if (!args.sectionId) {
				if (!article.customHomeSectionId) return
				await tx.mutate.helpCenterArticles.update({
					id: article.id,
					customHomeSectionId: null,
					homeOrder: null,
				})
				return
			}

			const section = await findOwnSection({ tx, ctx }, args.sectionId)
			if (section.groupId) {
				throw new Error("A folder section shows the folder's own articles.")
			}
			const inSection = (
				await tx.query.helpCenterArticles
					.where("workspaceId", ctx.workspaceId)
					.where("customHomeSectionId", section.id)
					.where("isDeleted", false)
					.run()
			).filter((other) => other.id !== article.id)
			if (inSection.length >= PICKED_SECTION_ARTICLE_LIMIT) {
				throw new Error("That section is full")
			}
			const lastOrder = inSection.reduce(
				(highest, other) => Math.max(highest, other.homeOrder ?? 0),
				-1,
			)
			// One home slot per article: picking it here takes it out of Get started or Popular.
			await tx.mutate.helpCenterArticles.update({
				id: article.id,
				customHomeSectionId: section.id,
				homeSection: null,
				homeOrder: lastOrder + 1,
				showOnHomePage: true,
			})
		},
	),
}
